import { Injectable, inject, DestroyRef } from '@angular/core'
import {
  Auth,
  GoogleAuthProvider,
  User,
  authState,
  MultiFactorResolver,
} from '@angular/fire/auth'

import { map, tap } from 'rxjs'
import { FIREBASE_AUTH_SDK } from './firebase-auth-sdk'

export class PasskeyClaimMissingError extends Error {}

export const MFA_SETTINGS_PATH = '/settings/security'

export type MfaIntent =
  | { type: 'add-sms' }
  | { type: 'add-totp' }
  | { type: 'add-passkey' }
  | { type: 'remove-passkey'; id: string }
  | { type: 'remove'; factorId: string }

export interface AuthUser {
  id: string
  name: string | null
  photoUrl: string | null
}

export interface LoginResult {
  success: boolean
  user?: User
  resolver?: MultiFactorResolver
  factorUid?: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly RETURN_URL_KEY = 'auth.returnUrl'
  private static readonly ENROLLMENT_ORIGIN_KEY = 'auth.mfaEnrollmentOrigin'
  private static readonly INTENT_KEY = 'auth.mfaIntent'

  private sdk = inject(FIREBASE_AUTH_SDK)
  private auth = inject(Auth)
  generation = 0
  private loginResult: LoginResult | null = null
  private verificationId: string = ''
  private phoneNumber: string = ''

  user: AuthUser | null = null
  user$ = authState(this.auth).pipe(
    map((user) => {
      return user ? this.mappingUser(user) : null
    }),
    tap((user) => {
      this.user = user
    }),
  )

  constructor() {
    let previousUid = this.auth.currentUser?.uid ?? null
    const unsubscribe = this.sdk.onAuthStateChanged(this.auth, (user) => {
      const uid = user?.uid ?? null
      if (uid !== previousUid) {
        this.generation++
        previousUid = uid
      }
    })
    inject(DestroyRef).onDestroy(unsubscribe)
  }

  async signInWithCustomToken(token: string): Promise<void> {
    const credential = await this.sdk.signInWithCustomToken(this.auth, token)
    const result = await this.sdk.getIdTokenResult(credential.user)
    if (result.claims['mfa_passkey'] !== true) {
      await this.signOutPreservingReturnUrl()
      this.redirectToLogin({ error: 'token_error' })
      throw new PasskeyClaimMissingError('Passkey claim missing')
    }
    this.generation++
  }

  applyActionCode(code: string): Promise<void> {
    return this.sdk.applyActionCode(this.auth, code)
  }

  waitForAuthStateReady(): Promise<void> {
    return this.auth.authStateReady()
  }

  async reloadCurrentUser(): Promise<void> {
    await this.requireCurrentUser().reload()
  }

  async sendEmailVerification(): Promise<void> {
    await this.sdk.sendEmailVerification(this.requireCurrentUser())
  }

  async hasPasskeyClaim(): Promise<boolean> {
    const token = await this.sdk.getIdTokenResult(this.requireCurrentUser())
    return token.claims['mfa_passkey'] === true
  }

  async getAuthTime(): Promise<string> {
    const token = await this.sdk.getIdTokenResult(this.requireCurrentUser())
    const firstFactorTime = token.claims['ff_auth_time']
    return typeof firstFactorTime === 'number'
      ? new Date(firstFactorTime * 1000).toISOString()
      : token.authTime
  }

  async unenrollFactor(id: string): Promise<void> {
    await this.sdk.multiFactor(this.requireCurrentUser()).unenroll(id)
  }

  private requireCurrentUser(): User {
    const user = this.auth.currentUser
    if (!user) throw new Error('No authenticated user')
    return user
  }

  /** Save the URL the user was trying to reach before being redirected to login. */
  setReturnUrl(url: string): void {
    if (url.startsWith('/')) {
      sessionStorage.setItem(AuthService.RETURN_URL_KEY, url)
    }
  }

  /** Read the saved return URL without clearing it. Returns null if none was stored or invalid. */
  peekReturnUrl(): string | null {
    const url = sessionStorage.getItem(AuthService.RETURN_URL_KEY)
    if (!url || !url.startsWith('/')) {
      return null
    }
    return url
  }

  /** Retrieve and clear the saved return URL. Returns null if none was stored or invalid. */
  consumeReturnUrl(): string | null {
    const url = this.peekReturnUrl()
    sessionStorage.removeItem(AuthService.RETURN_URL_KEY)
    return url
  }

  markMfaEnrollmentFromSettings(): void {
    sessionStorage.setItem(AuthService.ENROLLMENT_ORIGIN_KEY, 'settings')
  }

  isFromMfaSettings(): boolean {
    return (
      sessionStorage.getItem(AuthService.ENROLLMENT_ORIGIN_KEY) === 'settings'
    )
  }

  clearMfaEnrollmentOrigin(): void {
    sessionStorage.removeItem(AuthService.ENROLLMENT_ORIGIN_KEY)
  }

  setLoginResult(result: LoginResult): void {
    this.loginResult = result
  }

  getLoginResult(): LoginResult | null {
    return this.loginResult
  }

  setPhoneNumber(phoneNumber: string): void {
    this.phoneNumber = phoneNumber
  }

  getPhoneNumber(): string {
    return this.phoneNumber
  }

  setVerificationId(id: string): void {
    this.verificationId = id
  }

  getVerificationId(): string {
    return this.verificationId
  }

  loginWithGoogle = async (): Promise<LoginResult> => {
    try {
      const userCredential = await this.sdk.signInWithPopup(
        this.auth,
        new GoogleAuthProvider(),
      )
      const user = userCredential.user
      const idTokenResult = await this.sdk.getIdTokenResult(user)

      const claims = idTokenResult.claims
      if (!(claims.org_id && claims.user_id)) {
        console.error('Invalid claims:', claims)
        this.sdk.signOut(this.auth)
        return {
          success: false,
        }
      }
      return { success: true, user: userCredential.user }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = this.sdk.getMultiFactorResolver(this.auth, error)
        return {
          success: false,
          resolver: resolver,
        }
      }

      throw error
    }
  }

  loginWithEmailAndPassword = async (
    email: string,
    password: string,
  ): Promise<LoginResult> => {
    try {
      const userCredential = await this.sdk.signInWithEmailAndPassword(
        this.auth,
        email,
        password,
      )
      return { success: true, user: userCredential.user }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = this.sdk.getMultiFactorResolver(this.auth, error)
        return {
          success: false,
          resolver: resolver,
        }
      }

      throw error
    }
  }

  sendPasswordResetEmail = (email: string) => {
    return this.sdk.sendPasswordResetEmail(this.auth, email)
  }

  signOutPreservingReturnUrl(): Promise<void> {
    this.clearMfaEnrollmentOrigin()
    this.generation++
    this.loginResult = null
    this.verificationId = ''
    this.phoneNumber = ''
    return this.sdk.signOut(this.auth)
  }

  redirectToLogin(queryParams?: Record<string, string>): void {
    this.clearMfaEnrollmentOrigin()
    const query = new URLSearchParams(queryParams).toString()
    this.assignLocation(query ? `/login?${query}` : '/login')
  }

  /** 全画面再読み込みでの遷移。ブラウザで動く spec では差し替えて実際の遷移を止める */
  protected assignLocation(url: string): void {
    window.location.assign(url)
  }

  async startReauthentication(intent: MfaIntent): Promise<void> {
    this.setReturnUrl(MFA_SETTINGS_PATH)
    sessionStorage.setItem(
      AuthService.INTENT_KEY,
      JSON.stringify({ ...intent, uid: this.auth.currentUser?.uid }),
    )
    await this.signOutPreservingReturnUrl()
    this.redirectToLogin({ reason: 'reauth' })
  }

  consumePendingIntent(): MfaIntent | null {
    const value = sessionStorage.getItem(AuthService.INTENT_KEY)
    sessionStorage.removeItem(AuthService.INTENT_KEY)
    if (!value) return null
    try {
      const intent = JSON.parse(value)
      if (
        !intent ||
        !this.auth.currentUser ||
        intent.uid !== this.auth.currentUser.uid
      )
        return null
      if (intent.type === 'add-passkey') return { type: 'add-passkey' }
      if (intent.type === 'remove-passkey' && typeof intent.id === 'string')
        return { type: 'remove-passkey', id: intent.id }
      if (intent.type === 'add-sms') return { type: 'add-sms' }
      if (intent.type === 'add-totp') return { type: 'add-totp' }
      if (intent.type === 'remove' && typeof intent.factorId === 'string')
        return { type: 'remove', factorId: intent.factorId }
    } catch {
      return null
    }
    return null
  }

  logout = () => {
    this.clearMfaEnrollmentOrigin()
    this.generation++
    sessionStorage.removeItem(AuthService.INTENT_KEY)
    this.clearLoginState()
    return this.sdk.signOut(this.auth)
  }

  clearLoginState(): void {
    this.loginResult = null
    this.verificationId = ''
    this.phoneNumber = ''
    sessionStorage.removeItem(AuthService.RETURN_URL_KEY)
  }

  verifyPasswordResetCode = (oobCode: string): Promise<string> => {
    return this.sdk.verifyPasswordResetCode(this.auth, oobCode)
  }

  confirmPasswordReset = (
    oobCode: string,
    newPassword: string,
  ): Promise<void> => {
    return this.sdk.confirmPasswordReset(this.auth, oobCode, newPassword)
  }

  private mappingUser = (user: User): AuthUser => {
    return {
      id: user.uid,
      name: user.displayName,
      photoUrl: user.photoURL,
    }
  }
}
